import React, {useEffect, useMemo, useState} from "react"
import {Droppable} from "react-beautiful-dnd"
import {Menu, Dropdown, Button, Popover} from "antd"
import {useTranslation} from "react-i18next"
import {useVisible} from "hooks"
import {BaseButton} from "components/buttons"
import {Icon} from "components/Icon"
import {toastError} from "helpers"
import {Search} from "components/inputs/Search"
import {SectionItem} from "./SectionItem"
import styles from "./PhaseItem.module.css"
import {BaseInput, BaseLoading, KlassDropdown} from "components"
import {useDebounce} from "react-use"
import {useMutation, useQuery} from "@tanstack/react-query"
import {academicTrackPhasesAndShiftsTemplatesService, phasesService} from "services"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {DURATION_OPTIONS, PhaseDurationUnit} from "types/program-version-phases-and-shifts-templates"
import {AcademicTrackPhaseTemplateDetail} from "types/academic-track-phases-and-shifts-templates"
import SelectShiftPopup from "../../SelectShiftPopup"

const getBackground = (isDraggingOver) => {
    return isDraggingOver ? "#1f7fda" : "#a5d3ff"
}

const getListStyle = (isDraggingOver) => ({
    background: getBackground(isDraggingOver),
    padding: 8,
    minHeight: 100
})

type Props = {
    academicTrack: any
    phaseItem: AcademicTrackPhaseTemplateDetail
    reloadView: () => void
}

export function PhaseItem(props: Props) {
    const {phaseItem, academicTrack, reloadView} = props
    const [search, setSearch] = useState("")
    const popupSelectShift = useVisible()
    const [isOpenChoosePhase, setIsOpenChoosePhase] = useState(false)
    const {t} = useTranslation(["common"])
    const {shifts = []} = phaseItem ?? {}
    const [durationUnit, setDurationUnit] = useState<{id: PhaseDurationUnit; name: string}>()
    const [duration, setDuration] = useState(phaseItem.duration)

    const [searchDebounce, setSearchDebounce] = useState(search)

    useDebounce(() => setSearchDebounce(search), 500, [search])

    useEffect(() => {
        if (phaseItem) {
            const {durationUnit, duration} = phaseItem
            const durationUnitValue = DURATION_OPTIONS.find((item) => item.id === durationUnit)
            setDurationUnit(durationUnitValue)
            setDuration(duration)
        }
    }, [phaseItem])

    const getPhasesQuery = useQuery(
        ["get-academic-track-phases", searchDebounce],
        async () => {
            const {data} = await phasesService.getAll({
                filter: {
                    search: searchDebounce
                },
                range: {
                    page: 1,
                    pageSize: UNLIMITED_PAGE_SIZE
                }
            })
            return data.filter((phase) => phase.isActive)
        },
        {
            enabled: !!academicTrack,
            initialData: []
        }
    )

    const editPhaseMutation = useMutation(academicTrackPhasesAndShiftsTemplatesService.editPhaseTemplate, {
        onSuccess: () => {
            reloadView()
        },
        onError: () => {
            toastError("Failed to update phase")
        }
    })

    const deletePhaseMutation = useMutation(academicTrackPhasesAndShiftsTemplatesService.deletePhaseTemplate, {
        onSuccess: () => {
            reloadView()
        },
        onError: () => {
            toastError("Failed to remove phase")
        }
    })

    const isLoading = editPhaseMutation.isLoading || deletePhaseMutation.isLoading || getPhasesQuery.isLoading

    const onClickAddShifts = () => {
        popupSelectShift.open()
    }

    const removePhase = async () => {
        deletePhaseMutation.mutate({
            academicTrackPhaseShiftTemplateIds: [phaseItem.academicTrackPhaseShiftTemplateId]
        })
    }

    const onUpdateDuration = async () => {
        editPhaseMutation.mutate({
            phaseTemplates: [
                {
                    academicTrackPhaseShiftTemplateId: phaseItem.academicTrackPhaseShiftTemplateId,
                    phaseId: phaseItem.phaseId,
                    duration: Number(duration),
                    durationUnit: durationUnit.id
                }
            ]
        })
    }

    const onChoosePhase = async (phaseId: number) => {
        editPhaseMutation.mutate({
            phaseTemplates: [
                {
                    academicTrackPhaseShiftTemplateId: phaseItem.academicTrackPhaseShiftTemplateId,
                    phaseId,
                    position: phaseItem.position
                }
            ]
        })
        setIsOpenChoosePhase(false)
    }

    const content = (
        <div className={styles.termChooseWrap}>
            <Search onChange={setSearch} value={search} placeholder={t("common:action.search")} />
            <div className={styles.termChooseBody}>
                {isLoading ? (
                    <BaseLoading isShow />
                ) : (
                    getPhasesQuery.data.map((phase) => (
                        <p
                            className={styles.termChooseItem}
                            key={phase.phaseId}
                            onClick={() => onChoosePhase(phase.phaseId)}>
                            {phase.name}
                        </p>
                    ))
                )}
            </div>
        </div>
    )

    const isEnableSave = useMemo(() => {
        return (
            duration && durationUnit && (duration !== phaseItem.duration || durationUnit.id !== phaseItem.durationUnit)
        )
    }, [duration, durationUnit, phaseItem])

    const renderHeader = () => {
        const menu = (
            <Menu className={styles.menuActionWrap}>
                <Menu.Item key="delete" className={styles.menuActionItem} onClick={removePhase}>
                    <Icon className={styles.menuActionIcon} icon="DELETE" />
                    <span className={styles.menuActionTitle}>Delete</span>
                </Menu.Item>
            </Menu>
        )

        return (
            <div className={styles.termHeader}>
                <div className={styles.termHeaderName}>
                    <Popover
                        visible={isOpenChoosePhase}
                        content={content}
                        title="Choose the specific phase"
                        trigger="click"
                        onVisibleChange={setIsOpenChoosePhase}>
                        <div className={styles.chooseTitleWrap}>
                            <p className={styles.termTitle}>{phaseItem.phaseName}</p>
                            <span className={styles.chooseTitle__icon}>
                                <Icon icon="CHEVRON_DOWN" />
                            </span>
                        </div>
                    </Popover>

                    <div className={styles.termHeaderRight}>
                        <Dropdown overlay={menu} trigger={["click"]}>
                            <Button
                                className={styles.iconBtn}
                                icon={<Icon icon="MORE_VERTICAL" color="#939393" className={styles.moreIcon} />}
                            />
                        </Dropdown>
                    </div>
                </div>

                <div className={styles.durationBox}>
                    <BaseInput
                        value={duration}
                        type="number"
                        placeholder="Duration"
                        onChange={(value) => setDuration(value)}
                    />
                    <KlassDropdown
                        className={styles.durationUnit}
                        options={DURATION_OPTIONS}
                        value={durationUnit}
                        onChange={(value) => setDurationUnit(value)}
                        placeholder={"Unit"}
                    />
                    <BaseButton
                        title="SAVE"
                        className={styles.saveBtn}
                        onClick={onUpdateDuration}
                        disabled={!isEnableSave}
                        loading={editPhaseMutation.isLoading}
                    />
                </div>
            </div>
        )
    }

    const renderBody = () => {
        return (
            <div className={styles.termItemBodyWrap}>
                <SectionItem key={0} index={0} phaseItem={phaseItem} shifts={shifts} reloadView={reloadView} />
                <Droppable
                    droppableId={`phaseSectionEmpty_${phaseItem.academicTrackPhaseShiftTemplateId}`}
                    dropDisabled>
                    {(provided, snapshot) => (
                        <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        )
    }

    const renderFooter = () => {
        return (
            <div className={styles.termItemFooter}>
                <BaseButton title="Add Shifts" variant="secondary" onClick={onClickAddShifts} />
            </div>
        )
    }

    return (
        <div className={styles.termItem}>
            <div className={styles.termBody}>
                {renderHeader()}
                {renderBody()}
                {renderFooter()}
            </div>

            <SelectShiftPopup
                isVisible={popupSelectShift.isVisible}
                onClose={popupSelectShift.close}
                academicTrackId={academicTrack.trackId}
                academicTrackPhaseShiftTemplateId={phaseItem.academicTrackPhaseShiftTemplateId}
                onRefresh={reloadView}
            />
        </div>
    )
}
